import { merge } from "lodash";
import { type PromptVersion, MainPromptVersion } from "./assistant";
import { FieldType } from "./type-system/FieldType";

/**
 * The object describing all flag state for any context in the system
 * Flags are powered by LaunchDarkly. The LaunchDarkly flag is named after the path to the flag in this object, like `features.fieldTypes.Computed`.
 */
export type GadgetFlags = {
  features: {
    fieldTypes: {
      [FieldType.Computed]: boolean;
      [FieldType.Color]: boolean;
      [FieldType.Money]: boolean;
    };
    darkMode: boolean;
    ideJSErrorChecking: boolean;
    actionsAsCode: boolean;
    fileBasedActions: boolean;
    computedViews: boolean;
    codeMirrorShowAllErrors: boolean;
    codeMirrorHideAllErrors: boolean;
    computedFieldFiltering: boolean;
    editorAssistant: boolean;
    assistantReviewMode: "off" | "profile" | "on";
    assistantManagesTodos: boolean;
    assistantQuickReplies: boolean;
    stringEndsWithFilter: boolean;
    summonApps: boolean;
    scaffoldApps: boolean;
    hobbyUnrestricted: boolean;
    editorAIAutocomplete: boolean;
    runShopifyAppDeploy: boolean;
    customizeTemplate: boolean;
    opsDashboard: boolean;
    clickhouseLogs: boolean;
    assistantRoutePreviewScreenshots: boolean;
    hyperdxInDevHarness: boolean;
    relatedModelSearch: boolean;
    spendLimitAndNotification: boolean;
    chatGPTAppTemplate: boolean;
    redisBackedQueryLimiter: boolean;
    nodeDebugger: boolean;
    assistantTrimAppSummary: boolean;
  };
  internal: {
    assistantConversationCaching: boolean;
    assistantAgentTypeClassification: boolean;
    canarySandbox: boolean;
    stagingLoki: boolean;
    eluLimiterMode: "off" | "warn" | "enforce";
    rateLimitMode: "off" | "warn" | "enforce";
    requestRateLimitMode: "off" | "warn" | "enforce";
    allowBackPressureForActions: boolean;
    backgroundActionConcurrencyBackpressure: boolean;
    sendRateLimitErrorsToSandbox: boolean;
    databaseQueryRateLimitMode: "off" | "warn" | "enforce";
    sandboxDynamicActionTimeout: "off" | "warn" | "enforce";
    cutoverAppEmailsToSES: "off" | "mirror" | "on";
    throttleExchange: boolean;
    frameworkVersionAvailability: "released" | "beta" | "all";
    blackholeWebhooks: boolean;
    selectFrameworkVersionOnCreate: boolean;
    showProblemsInEditor: boolean;
    cloudflareTurnstile: boolean;
    usePreparedStatement: boolean;
    newEnvironmentShard: string;
    newProductionEnvironmentShard: string;
    inlineActionExecutor: boolean;
    useSpotForSandbox: boolean;
    useSpotForEligibleActions: boolean;
    searchIndex: boolean;
    aiPromptVersion: PromptVersion;
    assistantModelOverrides: Record<string, "codebase-default" | string>;
    runWebhookSchemaInference: boolean;
    liveQueryDiffDisabled: boolean;
    runWebhookMetafieldValidation: boolean;
    propagateAborts: boolean;
    useNginxBlue: boolean;
    redisBackedQueryLimiter: boolean;
    routeSandboxToC4D: boolean;
    assistantUseVertexForClaudeModels: boolean;
    planShopifyOnReadReplicas: boolean;
    resumableModelDataExport: boolean;
    shopLookupCache: boolean;
    backgroundActionCost: number;
    shopifyPIDRateLimiter: {
      kp?: number;
      ki?: number;
      kd?: number;
      outputMinRps?: number;
      outputMaxRps?: number;
      integralMin?: number;
      integralMax?: number;
      derivativeAlpha?: number;
      minDtSec?: number;
      targetUtilization?: number;
    } | null;
    captureLargeRequestBodies: boolean;
    supportAgentModel: string | null;
    newBillingAnnouncement: boolean;
    newBillingBackgroundEnqueue1Credit: boolean;
    newBillingSchedulerRun1Credit: boolean;
    newBilling10percentOff: boolean;
    newBilling2MPlatformCredits: boolean;
    newBillingCustomDiscounts: { discounts?: number[]; customOrbPlanId?: string };
  };
};

/** Default flag state used for tests in state-trees and web */
export const TestFlagDefaultState: GadgetFlags = {
  features: {
    fieldTypes: {
      [FieldType.Computed]: false,
      [FieldType.Color]: false,
      [FieldType.Money]: false,
    },
    darkMode: false,
    ideJSErrorChecking: false,
    actionsAsCode: true,
    fileBasedActions: true,
    computedViews: true,
    codeMirrorShowAllErrors: false,
    codeMirrorHideAllErrors: false,
    computedFieldFiltering: true,
    editorAssistant: true,
    assistantReviewMode: "off",
    assistantManagesTodos: false,
    assistantQuickReplies: false,
    stringEndsWithFilter: true,
    summonApps: false,
    scaffoldApps: false,
    hobbyUnrestricted: false,
    editorAIAutocomplete: false,
    runShopifyAppDeploy: false,
    customizeTemplate: false,
    opsDashboard: false,
    clickhouseLogs: false,
    assistantRoutePreviewScreenshots: false,
    hyperdxInDevHarness: false,
    relatedModelSearch: false,
    spendLimitAndNotification: false,
    chatGPTAppTemplate: false,
    redisBackedQueryLimiter: false,
    nodeDebugger: true,
    assistantTrimAppSummary: false,
  },
  internal: {
    assistantConversationCaching: false,
    assistantAgentTypeClassification: true,
    eluLimiterMode: "off",
    canarySandbox: false,
    stagingLoki: false,
    rateLimitMode: "enforce",
    requestRateLimitMode: "off",
    allowBackPressureForActions: false,
    backgroundActionConcurrencyBackpressure: false,
    sendRateLimitErrorsToSandbox: false,
    databaseQueryRateLimitMode: "off",
    sandboxDynamicActionTimeout: "off",
    cutoverAppEmailsToSES: "off",
    throttleExchange: false,
    frameworkVersionAvailability: "released",
    blackholeWebhooks: false,
    selectFrameworkVersionOnCreate: false,
    showProblemsInEditor: true,
    cloudflareTurnstile: false,
    usePreparedStatement: false,
    newEnvironmentShard: "main",
    newProductionEnvironmentShard: "main",
    inlineActionExecutor: false,
    useSpotForSandbox: false,
    useSpotForEligibleActions: false,
    searchIndex: true,
    aiPromptVersion: MainPromptVersion,
    assistantModelOverrides: {
      topLevelAgent: "codebase-default",
      baseFastSimple: "codebase-default",
      baseReasoning: "codebase-default",
      baseFastReasoning: "codebase-default",
      dataModeller: "codebase-default",
      codeGenerator: "codebase-default",
      codeEvaluator: "codebase-default",
      changeSelector: "codebase-default",
      codeDiffApplier: "codebase-default",
      summarizer: "codebase-default",
    },
    runWebhookSchemaInference: false,
    liveQueryDiffDisabled: false,
    runWebhookMetafieldValidation: false,
    propagateAborts: true,
    useNginxBlue: false,
    redisBackedQueryLimiter: false,
    routeSandboxToC4D: false,
    assistantUseVertexForClaudeModels: false,
    planShopifyOnReadReplicas: false,
    resumableModelDataExport: false,
    shopLookupCache: false,
    backgroundActionCost: 1,
    shopifyPIDRateLimiter: null,
    captureLargeRequestBodies: false,
    supportAgentModel: null,
    newBillingAnnouncement: false,
    newBillingBackgroundEnqueue1Credit: false,
    newBillingSchedulerRun1Credit: false,
    newBilling10percentOff: false,
    newBilling2MPlatformCredits: false,
    newBillingCustomDiscounts: {},
  },
};

export const ClientSideDefaultFlags: GadgetFlags = merge({}, TestFlagDefaultState, {});
